import React, { createContext, useContext, useMemo, useState, useCallback } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  ColumnDef,
  SortingState,
  VisibilityState,
  flexRender,
  Table as TanstackTable,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Filter, ChevronLeft, ChevronRight, ChevronRightIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { LAYOUT_VIEWPORT_HEIGHT } from '@/lib/constants/layout';
import { ColumnVisibilityToggle } from '@/components/table/column-visibility';
import { TableRowSkeletons } from '@/components/table/row-skeletons';
import type { CSSProperties } from 'react';

// Types
export interface DataTableConfig<TData> {
  columns: ColumnDef<TData>[];
  data: TData[];
  loading?: boolean;
  error?: Error | null;
  // Pagination
  page: number;
  pageSize: number;
  totalCount?: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
  // State
  stateKey?: string;
  defaultColumnVisibility?: VisibilityState;
  onColumnVisibilityChange?: (visibility: VisibilityState) => void;
  defaultSorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
  // UI
  emptyMessage?: string;
}

interface DataTableContextValue<TData> {
  table: TanstackTable<TData>;
  config: DataTableConfig<TData>;
  // Sidebar state
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebar: () => void;
  // Column visibility
  columnVisibility: VisibilityState;
  setColumnVisibility: (visibility: VisibilityState) => void;
  // Sorting
  sorting: SortingState;
  setSorting: (sorting: SortingState) => void;
}

const DataTableContext = createContext<DataTableContextValue<unknown> | null>(null);

function useDataTableContext<TData>() {
  const context = useContext(DataTableContext) as DataTableContextValue<TData> | null;
  if (!context) {
    throw new Error('DataTable components must be used within a DataTable provider');
  }
  return context;
}

// Main DataTable provider
export interface DataTableProps<TData> extends DataTableConfig<TData> {
  children: React.ReactNode;
  /** Default sidebar open state (uncontrolled) */
  defaultSidebarOpen?: boolean;
  /** Controlled sidebar open state */
  sidebarOpen?: boolean;
  /** Callback when sidebar state changes (for controlled mode) */
  onSidebarOpenChange?: (open: boolean) => void;
}

export function DataTable<TData>({
  children,
  defaultSidebarOpen = false,
  sidebarOpen: controlledSidebarOpen,
  onSidebarOpenChange,
  ...config
}: DataTableProps<TData>) {
  const [internalSidebarOpen, setInternalSidebarOpen] = useState(defaultSidebarOpen);

  // Support both controlled and uncontrolled modes
  const isControlled = controlledSidebarOpen !== undefined;
  const sidebarOpen = isControlled ? controlledSidebarOpen : internalSidebarOpen;
  const setSidebarOpen = useCallback(
    (open: boolean) => {
      if (isControlled) {
        onSidebarOpenChange?.(open);
      } else {
        setInternalSidebarOpen(open);
      }
    },
    [isControlled, onSidebarOpenChange]
  );
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    config.defaultColumnVisibility ?? {}
  );
  const [sorting, setSorting] = useState<SortingState>(config.defaultSorting ?? []);

  const table = useReactTable({
    data: config.data,
    columns: config.columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    state: {
      columnVisibility,
      sorting,
    },
    onColumnVisibilityChange: (updater) => {
      const newVisibility = typeof updater === 'function' ? updater(columnVisibility) : updater;
      setColumnVisibility(newVisibility);
      config.onColumnVisibilityChange?.(newVisibility);
    },
    onSortingChange: (updater) => {
      const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
      setSorting(newSorting);
      config.onSortingChange?.(newSorting);
    },
  });

  const toggleSidebar = useCallback(
    () => setSidebarOpen(!sidebarOpen),
    [sidebarOpen, setSidebarOpen]
  );

  const contextValue = useMemo(
    () => ({
      table,
      config,
      sidebarOpen,
      setSidebarOpen,
      toggleSidebar,
      columnVisibility,
      setColumnVisibility,
      sorting,
      setSorting,
    }),
    [table, config, sidebarOpen, toggleSidebar, columnVisibility, sorting]
  );

  return (
    <DataTableContext.Provider value={contextValue as DataTableContextValue<unknown>}>
      {children}
    </DataTableContext.Provider>
  );
}

export interface DataTableInlineControlsProps {
  showFilterToggle?: boolean;
  showColumnVisibility?: boolean;
  className?: string;
}

export function DataTableInlineControls({
  showFilterToggle = true,
  showColumnVisibility = true,
  className,
}: DataTableInlineControlsProps) {
  const { table, toggleSidebar, sidebarOpen } = useDataTableContext();

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {showColumnVisibility && <ColumnVisibilityToggle table={table} />}
      {showFilterToggle && !sidebarOpen && (
        <Button variant="outline" size="sm" onClick={toggleSidebar}>
          <Filter className="mr-2 h-4 w-4" />
          Filters
        </Button>
      )}
    </div>
  );
}

// Container - flex layout for content + sidebar
export function DataTableContainer({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'relative flex flex-col-reverse gap-0 md:flex-row-reverse md:items-start md:gap-4',
        className
      )}
      style={{ minHeight: LAYOUT_VIEWPORT_HEIGHT }}
    >
      {children}
    </div>
  );
}

// Filter sidebar wrapper - sticky to page with internal scroll
export function DataTableFilterBar({
  children,
  className,
  sticky = true,
}: {
  children: React.ReactNode;
  className?: string;
  sticky?: boolean;
}) {
  const { sidebarOpen } = useDataTableContext();

  if (!sidebarOpen) return null;

  return (
    <div
      className={cn(
        'flex flex-col self-start border-l border-[#2E3835] pt-3 md:w-[320px] md:shrink-0',
        sticky && 'md:sticky md:top-[3.5rem]',
        className
      )}
      style={sticky ? { height: LAYOUT_VIEWPORT_HEIGHT, minHeight: LAYOUT_VIEWPORT_HEIGHT } : {}}
    >
      <div className={cn('flex-1', sticky && 'overflow-y-auto')}>{children}</div>
    </div>
  );
}

// Main content area
export function DataTableContent({
  children,
  className,
  sticky = true,
}: {
  children: React.ReactNode;
  className?: string;
  sticky?: boolean;
}) {
  return (
    <div className={cn('flex min-h-0 flex-1 flex-col', sticky && 'h-full', className)}>
      {children}
    </div>
  );
}

export interface DataTablePageHeaderProps {
  title: string;
  icon?: React.ReactNode;
  actions?: React.ReactNode;
  search?: {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
  };
  className?: string;
}

export function DataTablePageHeader({
  title,
  icon,
  actions,
  search,
  className,
}: DataTablePageHeaderProps) {
  return (
    <div
      className={cn(
        'flex flex-col gap-3 border-b border-[#2E3835] bg-card1/90 px-4 py-3 md:flex-row md:items-center md:justify-between',
        className
      )}
    >
      <div className="flex items-center gap-3">
        {icon}
        <h1 className="text-xl font-semibold text-white">{title}</h1>
      </div>
      <div className="flex flex-1 flex-col gap-2 md:flex-row md:items-center md:justify-end">
        {search && (
          <Input
            type="text"
            placeholder={search.placeholder || 'Search...'}
            value={search.value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => search.onChange(e.target.value)}
            className="md:max-w-xs"
            isSearch
          />
        )}
        {actions}
      </div>
    </div>
  );
}

// Utility bar with filter toggle, column visibility, etc.
export interface DataTableUtilityBarProps {
  title?: string;
  showFilterToggle?: boolean;
  showColumnVisibility?: boolean;
  /** Search input configuration */
  search?: {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
  };
  filterCount?: number;
  actions?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  sticky?: boolean;
}

export function DataTableUtilityBar({
  title,
  showFilterToggle = true,
  showColumnVisibility = true,
  search,
  filterCount,
  actions,
  children,
  className,
  sticky = true,
}: DataTableUtilityBarProps) {
  const { table, toggleSidebar, sidebarOpen } = useDataTableContext();

  return (
    <div
      className={cn(
        'flex flex-col gap-3 py-2 px-4 md:flex-row md:items-center md:justify-between',
        sticky && 'sticky top-12',
        className
      )}
    >
      <div className="flex flex-1 flex-col gap-2 md:flex-row md:items-center md:gap-3">
        {title && <h2 className="text-lg font-semibold text-white">{title}</h2>}
        <div className="flex items-center gap-x-2">
          {search && (
            <Input
              type="text"
              placeholder={search.placeholder || 'Search...'}
              value={search.value}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => search.onChange(e.target.value)}
              className="w-[400px] md:max-w-sm"
              isSearch
            />
          )}
          {children}
        </div>
      </div>
      <div className="flex items-center justify-end gap-2">
        {showColumnVisibility && <ColumnVisibilityToggle table={table} />}
        {showFilterToggle && !sidebarOpen && (
          <Button
            variant="outline"
            size="sm"
            onClick={toggleSidebar}
            className="flex items-center gap-2"
          >
            <Filter className="h-4 w-4" />
            <span className="flex items-center gap-1">
              Filters
              {filterCount !== undefined && filterCount > 0 && (
                <span className="inline-flex items-center rounded-full bg-green/40 px-1.5 text-xs font-semibold text-white">
                  {filterCount}
                </span>
              )}
            </span>
          </Button>
        )}
        {actions}
      </div>
    </div>
  );
}

// Table header - sticky within scroll container
export function DataTableHeader({
  className,
  sticky = true,
}: {
  className?: string;
  sticky?: boolean;
}) {
  const { table } = useDataTableContext();

  return (
    <thead
      className={cn(
        'z-10 bg-card1/90 backdrop-blur [&_tr]:border-b',
        sticky && 'sticky top-0',
        className
      )}
    >
      {table.getHeaderGroups().map((headerGroup) => (
        <TableRow key={headerGroup.id}>
          {headerGroup.headers.map((header) => (
            <TableHead
              key={header.id}
              style={{
                textAlign: (header.column.columnDef.meta as { align?: CSSProperties['textAlign'] })
                  ?.align,
              }}
              className={cn(
                header.column.getCanSort() && 'cursor-pointer select-none',
                'whitespace-nowrap first:rounded-tl-lg last:rounded-tr-lg',
                sticky && 'bg-card1/90 backdrop-blur'
              )}
              onClick={header.column.getToggleSortingHandler()}
            >
              {header.isPlaceholder
                ? null
                : flexRender(header.column.columnDef.header, header.getContext())}
              {header.column.getIsSorted() === 'asc' && ' ↑'}
              {header.column.getIsSorted() === 'desc' && ' ↓'}
            </TableHead>
          ))}
        </TableRow>
      ))}
    </thead>
  );
}

// Table rows
export interface DataTableRowsProps {
  className?: string;
  skeletonRows?: number;
}

export function DataTableRows({ className, skeletonRows = 10 }: DataTableRowsProps) {
  const { table, config } = useDataTableContext();
  const { loading, emptyMessage = 'No results found' } = config;

  if (loading) {
    return (
      <TableBody className={className}>
        <TableRowSkeletons table={table} rowCount={skeletonRows} />
      </TableBody>
    );
  }

  const rows = table.getRowModel().rows;

  if (rows.length === 0) {
    return (
      <TableBody className={className}>
        <TableRow>
          <TableCell
            colSpan={table.getAllColumns().length}
            className="h-24 text-center text-muted-foreground"
          >
            {emptyMessage}
          </TableCell>
        </TableRow>
      </TableBody>
    );
  }

  return (
    <TableBody className={className}>
      {rows.map((row) => (
        <TableRow
          key={row.id}
          data-state={row.getIsSelected() && 'selected'}
          className="transition-colors duration-75 hover:bg-muted/5"
        >
          {row.getVisibleCells().map((cell) => (
            <TableCell
              key={cell.id}
              style={{
                textAlign: (cell.column.columnDef.meta as { align?: CSSProperties['textAlign'] })
                  ?.align,
              }}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </TableCell>
          ))}
        </TableRow>
      ))}
    </TableBody>
  );
}

// Table wrapper with border/styling - allows internal scroll for sticky header
export function DataTableWrapper({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'relative mx-4 mt-0 min-h-0 flex-1 overflow-x-auto rounded-t-xl border bg-card1',
        className
      )}
    >
      <table className="w-full caption-bottom text-sm">{children}</table>
    </div>
  );
}

// Footer with pagination
export interface DataTableFooterProps {
  className?: string;
}

export function DataTableFooter({ className }: DataTableFooterProps) {
  const { config } = useDataTableContext();
  const {
    page,
    pageSize,
    totalCount,
    onPageChange,
    onPageSizeChange,
    pageSizeOptions = [10, 20, 50, 100],
  } = config;

  const totalPages = totalCount ? Math.ceil(totalCount / pageSize) : 1;
  const canPreviousPage = page > 1;
  const canNextPage = page < totalPages;

  return (
    <div
      className={cn(
        'sticky bottom-0 mx-4 flex items-center justify-center border-x border-t bg-card1/90 px-4 py-2 text-sm text-white backdrop-blur-sm gap-4',
        className
      )}
    >   <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page - 1)}
          disabled={!canPreviousPage}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        {onPageSizeChange && (
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="rounded border bg-transparent px-1.5 py-1.5 text-sm"
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        )}
        {totalCount !== undefined && (
          <span>
            {totalCount === 0
              ? '0 results'
              : `${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, totalCount)} of ${totalCount}`}
          </span>
        )}
      </div>
         <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page + 1)}
          disabled={!canNextPage}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
    </div>
  );
}

// Export hook for custom components
export { useDataTableContext };
